import React, { useReducer, useMemo, useCallback, useEffect } from 'react';
import StepIndicator from './StepIndicator';
import { WizardContext } from '../../../context/WizardContext';
import { wizardReducer } from './reducer';

/**
 * Wizard — A reusable component for building multi-step flows in React.
 *
 * Example:
 *
 * import Wizard from './Wizard';
 * import { useWizard } from '../context/WizardContext';
 *
 * const MyStep = () => {
 *   const { next, prev, goTo, setStepValid, completeStep, currentId, canProceed } = useWizard();
 *   // ... your step UI, call setStepValid(true/false) when input changes
 *   // call completeStep(currentId) then next() or goTo('someStepId') when user finishes
 * };
 *
 * <Wizard
 *   steps={[
 *     { id: 'info',    label: 'Information' },
 *     { id: 'details', label: 'Details' },
 *     { id: 'confirm', label: 'Confirm' },
 *   ]}
 *   initialStep={0}
 *   indicatorProps={{
 *     // Pass custom Tailwind classes or override onStepClick behavior here
 *     className: 'bg-white shadow',
 *   }}
 * >
 *   <StepInfo />
 *   <StepDetails />
 *   <StepConfirm />
 * </Wizard>
 */

/**
 *
 * @param {Array} steps - Array of step definitions: { id: unique string, label: displayed text }
 * @param {Number} initialStep (0‐based) of the step to display first (defaults to 0)
 * @param {ReactNode} children - One child per step, in the same order as `steps`
 * @param {object} indicatorProps - Extra props to pass to StepIndicator (e.g. custom styles or callbacks)
 * @returns
 */

const Wizard = ({
  steps,
  initialStep = 0,
  children,
  indicatorProps,
  onStepChange,
  onStatusChange,
  initialCompletedIds = [],
  initialVisitedIds = [],
}) => {
  const [state, dispatch] = useReducer((s, a) => wizardReducer(s, a, steps), {
    total: steps.length,
    currentIndex: initialStep,
    currentId: steps[initialStep].id,
    visited: new Set(initialVisitedIds.length ? initialVisitedIds : [steps[initialStep].id]),
    completed: initialCompletedIds,
    canProceed: false,
  });

  useEffect(() => {
    onStepChange?.(state.currentIndex, state.currentId);
  }, [state.currentIndex, state.currentId, onStepChange]);

  useEffect(() => {
    onStatusChange?.({
      completedIds: state.completed,
      visitedIds: [...state.visited],
    });
  }, [state.completed, state.visited, onStatusChange]);

  const next = useCallback(() => dispatch({ type: 'NEXT' }), []);
  const prev = useCallback(() => dispatch({ type: 'PREV' }), []);
  const goTo = useCallback((id) => dispatch({ type: 'GOTO', id }), []);
  const setStepValid = useCallback((v) => dispatch({ type: 'STEP_VALID', valid: v }), []);
  const completeStep = useCallback((id) => dispatch({ type: 'STEP_COMPLETED', id }), []);

  const api = useMemo(
    () => ({
      currentId: state.currentId,
      currentIndex: state.currentIndex,
      totalSteps: state.total,
      visitedIds: [...state.visited],
      completedIds: state.completed,
      next,
      prev,
      goTo,
      setStepValid,
      completeStep,
      canProceed: state.canProceed,
    }),
    [state, next, prev, goTo, setStepValid, completeStep],
  );

  return (
    <WizardContext.Provider value={api}>
      <div className="flex flex-col">
        <StepIndicator
          steps={steps}
          currentId={state.currentId}
          completedIds={state.completed}
          onStepClick={goTo}
          {...indicatorProps}
        />
        <div>{React.Children.toArray(children)[state.currentIndex]}</div>
      </div>
    </WizardContext.Provider>
  );
};

export default Wizard;
